import React from 'react';
import { Card, CardContent } from '@/Components/ui/card';
import { Text } from '@/Components/ui/typography';
import { Stack } from '@/Components/ui/layout';
import { Popover, PopoverContent, PopoverTrigger } from '@/Components/ui/popover';
import { cn } from '@/lib/utils';

// ─────────────────────────────────────────────────────────────────────────────
// Fit-status logic — exported so Reports.jsx can reuse the same rules
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Derive the fit label from all available test scores + plagiarism.
 *
 * Rules (in priority order):
 *  1. High plagiarism (>40%)  → cap at "Moderate Fit"
 *  2. Any score < 30          → "Poor Fit"
 *  3. Any score < 50          → cap at "Moderate Fit"
 *  4. Average ≥ 75 + all ≥ 50 → "Strong Fit"
 *  5. Average ≥ 55            → "Moderate Fit"
 *  6. Average ≥ 35            → "Weak Fit"
 *  7. Else                    → "Poor Fit"
 */
export function computeFitStatus(avgScore, scores, plagiarismPct) {
  const taken = scores.filter(s => s != null);
  const highPlagiarism = plagiarismPct != null && plagiarismPct > 40;
  const anyVeryLow     = taken.some(s => s < 30);
  const anyLow         = taken.some(s => s < 50);

  if (anyVeryLow)     return 'Poor Fit';
  if (highPlagiarism) return avgScore >= 55 ? 'Moderate Fit' : 'Weak Fit';
  if (anyLow)         return avgScore >= 55 ? 'Moderate Fit' : 'Weak Fit';

  if (avgScore >= 75) return 'Strong Fit';
  if (avgScore >= 55) return 'Moderate Fit';
  if (avgScore >= 35) return 'Weak Fit';
  return 'Poor Fit';
}

const FIT_BADGE_STYLES = {
  'Strong Fit':   'bg-green-100  text-green-800  border-green-300',
  'Moderate Fit': 'bg-blue-100   text-blue-800   border-blue-300',
  'Weak Fit':     'bg-yellow-100 text-yellow-800 border-yellow-300',
  'Poor Fit':     'bg-red-100    text-red-800    border-red-300',
};

// ─────────────────────────────────────────────────────────────────────────────
// Section meta
// ─────────────────────────────────────────────────────────────────────────────

const SECTION_META = {
  'Technical Screening': {
    description: 'Score from the domain knowledge screening test',
    icon: '/technical-support.png',
    color: '#3f51b5',
  },
  'Non Technical': {
    description: 'Score from personality and behavioural assessment',
    icon: '/mind-map.png',
    color: '#ffc107',
  },
  Coding: {
    description: 'Score from coding evaluation',
    icon: '/code.png',
    color: '#4caf50',
  },
  Communication: {
    description: 'Score from communication skills assessment',
    icon: '/communication.png',   // add this asset, or swap for a lucide icon
    color: '#9c27b0',
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const CustomPieChart = ({ scores, noCard = false, onCodingClick, onScreeningClick, onCommunicationClick, onNonTechnicalClick }) => {
  if (!scores) return <div>No score data available.</div>;

  const plagiarism = scores.plagiarism_percentage != null
    ? parseFloat(scores.plagiarism_percentage)
    : null;

  const highPlagiarism = plagiarism != null && plagiarism > 40;

  // ── derive non-technical average ──────────────────────────────────────────
  const nonTechValue = (() => {
    const p = typeof scores.personality_score  === 'number' ? scores.personality_score  : null;
    const b = typeof scores.behavioral_score   === 'number' ? scores.behavioral_score   : null;
    if (p != null && b != null) return (p + b) / 2;
    if (p != null) return p;
    if (b != null) return b;
    return 0;
  })();

  // ── build section list ────────────────────────────────────────────────────
  const allSections = [
    {
      label: 'Technical Screening',
      value: typeof scores.screening_score     === 'number' ? scores.screening_score     : 0,
    },
    {
      label: 'Non Technical',
      value: nonTechValue,
    },
    {
      label: 'Coding',
      value: typeof scores.coding_score        === 'number' ? scores.coding_score        : 0,
    },
    {
      label: 'Communication',
      value: typeof scores.communication_score === 'number' ? scores.communication_score : 0,
    },
  ];

  const sections = allSections
    .map(s => ({ ...s, ...SECTION_META[s.label] }))
    .filter(s => s.value > 0);

  const total = sections.reduce((sum, s) => sum + s.value, 0);

  if (sections.length === 0 || total === 0) {
    return <div className="text-sm text-muted-foreground">No score data available for this candidate.</div>;
  }

  // ── fit status ────────────────────────────────────────────────────────────
  const avg = Math.round(total / sections.length);
  const individualScores = allSections.map(s => s.value > 0 ? s.value : null);
  const fitStatus = computeFitStatus(avg, individualScores, plagiarism);
  const fitBadgeClass = FIT_BADGE_STYLES[fitStatus];

  // ── pie arcs ──────────────────────────────────────────────────────────────
  let cumulativeAngle = 0;
  const RADIUS = 100;

  // ── legend row ────────────────────────────────────────────────────────────
  const LegendRow = ({ s }) => {
    const isCodingWithPlagiarism = s.label === 'Coding' && highPlagiarism;
    const isCoding    = s.label === 'Coding';
    const isScreening = s.label === 'Technical Screening';
    const isCommunication = s.label === 'Communication';
    const isNonTechnical = s.label === 'Non Technical';

    // Clickable row helper — Coding, Screening, Communication and P&B open a detail modal
    const clickHandler = isCoding && onCodingClick
      ? onCodingClick
      : isScreening && onScreeningClick
      ? onScreeningClick
      : isCommunication && onCommunicationClick
      ? onCommunicationClick
      : isNonTechnical && onNonTechnicalClick
      ? onNonTechnicalClick
      : null;

    if (clickHandler) {
      const accentBorder = isCoding ? 'hover:border-violet-200' : isCommunication ? 'hover:border-emerald-200' : isNonTechnical ? 'hover:border-amber-200' : 'hover:border-blue-200';
      const accentText   = isCoding ? 'text-violet-600 dark:text-violet-400' : isCommunication ? 'text-emerald-600 dark:text-emerald-400' : isNonTechnical ? 'text-amber-600 dark:text-amber-400' : 'text-blue-600 dark:text-blue-400';
      return (
        <div
          onClick={clickHandler}
          className={cn(
            'flex items-center gap-3 rounded-md py-2 px-3 transition-all cursor-pointer relative group',
            'hover:scale-[1.01]',
            noCard ? 'w-full min-w-0' : 'min-w-[280px]',
            isCodingWithPlagiarism
              ? 'bg-red-50 border border-red-200 hover:bg-red-100'
              : `bg-muted/50 hover:bg-muted border border-transparent ${accentBorder}`,
          )}
        >
          <div
            className={cn(
              'rounded-full flex items-center justify-center text-white font-semibold shadow-sm flex-shrink-0',
              noCard ? 'w-8 h-8' : 'w-9 h-9',
            )}
            style={{ backgroundColor: isCodingWithPlagiarism ? '#ef4444' : s.color }}
          >
            <img src={s.icon} alt={s.label} className="w-5 h-5" />
          </div>
          <div className={cn('text-foreground font-medium flex-1 min-w-0', noCard ? 'text-sm' : 'text-base')}>
            <span className="font-semibold">{s.label}</span>{' '}
            <span className={isCodingWithPlagiarism ? 'text-red-600 font-semibold' : 'text-muted-foreground'}>
              {s.value || 0}%
            </span>
            {isCoding && scores.coding_language && (
              <><br /><span className="text-xs text-muted-foreground">Language — {scores.coding_language}</span></>
            )}
            {isCoding && plagiarism != null && (
              <><br /><span className={cn('text-xs font-medium', highPlagiarism ? 'text-red-600' : 'text-muted-foreground')}>
                {highPlagiarism ? '⚠ ' : ''}Plagiarism — {plagiarism}% match
              </span></>
            )}
            {isScreening && scores.screening_difficulty && (
              <><br /><span className="text-xs text-muted-foreground">Difficulty: {scores.screening_difficulty}</span></>
            )}
          </div>
          {isCodingWithPlagiarism && (
            <span className="text-[10px] font-bold uppercase tracking-wide bg-red-500 text-white rounded px-1.5 py-0.5 flex-shrink-0">High</span>
          )}
          <span className={cn('text-[10px] font-semibold opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 pr-0.5', accentText)}>
            View →
          </span>
        </div>
      );
    }

    return (
      <Popover>
        <PopoverTrigger asChild>
          <div className={cn(
            'flex items-center gap-3 rounded-md py-2 px-3 transition-all cursor-pointer relative',
            'hover:scale-[1.01]',
            noCard ? 'w-full min-w-0' : 'min-w-[280px]',
            isCodingWithPlagiarism
              ? 'bg-red-50 border border-red-200 hover:bg-red-100'
              : 'bg-muted/50 hover:bg-muted',
          )}>
            {/* Colour dot / icon */}
            <div
              className={cn(
                'rounded-full flex items-center justify-center text-white font-semibold shadow-sm flex-shrink-0',
                noCard ? 'w-8 h-8' : 'w-9 h-9',
              )}
              style={{ backgroundColor: isCodingWithPlagiarism ? '#ef4444' : s.color }}
            >
              <img src={s.icon} alt={s.label} className="w-5 h-5" />
            </div>

            {/* Text */}
            <div className={cn('text-foreground font-medium flex-1 min-w-0', noCard ? 'text-sm' : 'text-base')}>
              <span className="font-semibold">{s.label}</span>{' '}
              <span className={isCodingWithPlagiarism ? 'text-red-600 font-semibold' : 'text-muted-foreground'}>
                {s.value || 0}%
              </span>

              {s.label === 'Technical Screening' && scores.screening_difficulty && (
                <><br /><span className="text-xs text-muted-foreground">Difficulty: {scores.screening_difficulty}</span></>
              )}

              {s.label === 'Coding' && (
                <>
                  {scores.coding_language && (
                    <><br /><span className="text-xs text-muted-foreground">Language — {scores.coding_language}</span></>
                  )}
                  {plagiarism != null && (
                    <>
                      <br />
                      <span className={cn('text-xs font-medium', highPlagiarism ? 'text-red-600' : 'text-muted-foreground')}>
                        {highPlagiarism ? '⚠ ' : ''}Plagiarism — {plagiarism}% match
                      </span>
                    </>
                  )}
                </>
              )}
            </div>

            {/* High-plagiarism pill */}
            {isCodingWithPlagiarism && (
              <span className="text-[10px] font-bold uppercase tracking-wide bg-red-500 text-white rounded px-1.5 py-0.5 flex-shrink-0">
                High
              </span>
            )}
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-auto max-w-xs">
          <Text size="sm">{s.description}</Text>
          {isCodingWithPlagiarism && (
            <Text size="xs" className="text-red-600 mt-1 font-medium">
              Plagiarism above threshold — review recommended.
            </Text>
          )}
        </PopoverContent>
      </Popover>
    );
  };

  const chartContent = (
    <div className={cn('flex justify-center items-center w-full gap-4', noCard && 'gap-4')}>
      {/* Legend */}
      <Stack spacing={noCard ? 'sm' : 'md'} className={cn('min-w-[160px]', noCard && 'flex-1 min-w-[180px]')}>
        {sections.map((s, i) => <LegendRow key={i} s={s} />)}

        {/* Fit badge */}
        <div className="mt-1 flex justify-start">
          <span className={cn(
            'text-xs font-semibold px-2.5 py-1 rounded-full border',
            fitBadgeClass,
          )}>
            {fitStatus}
          </span>
        </div>

        {/* High plagiarism warning banner */}
        {highPlagiarism && (
          <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 mt-1">
            <p className="text-xs text-red-700 font-medium leading-snug">
              ⚠ High plagiarism detected ({plagiarism}%).
            </p>
          </div>
        )}
      </Stack>

      {/* Donut chart */}
      <div className="flex-shrink-0">
        <svg
          viewBox="0 0 320 320"
          className={cn('transition-transform block mx-auto hover:scale-105', noCard ? 'w-[220px] h-[220px]' : 'w-[230px] h-[230px]')}
          width={noCard ? 220 : 300}
          height={noCard ? 220 : 320}
        >
          <defs>
            <filter id="pieShadow" x="-30%" y="-30%" width="160%" height="160%">
              <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="#000" floodOpacity="0.12" />
            </filter>
          </defs>
          <g transform="translate(160,160)">
            {sections.map((s, i) => {
              const angle       = (s.value / total) * 360;
              const startAngle  = cumulativeAngle;
              const endAngle    = cumulativeAngle + angle;
              cumulativeAngle  += angle;

              const toRad = (deg) => (Math.PI / 180) * deg;
              const x1 = RADIUS * Math.cos(toRad(startAngle));
              const y1 = RADIUS * Math.sin(toRad(startAngle));
              const x2 = RADIUS * Math.cos(toRad(endAngle));
              const y2 = RADIUS * Math.sin(toRad(endAngle));
              const largeArc = angle > 180 ? 1 : 0;
              const gradId = `pieGrad${i}`;

              // Use red for high-plagiarism coding slice
              const isCodingHighPlag = s.label === 'Coding' && highPlagiarism;
              const sliceColor = isCodingHighPlag ? '#ef4444' : s.color;

              return (
                <g key={i}>
                  <defs>
                    <linearGradient id={gradId} x1="0" y1="0" x2={x2} y2={y2}>
                      <stop offset="0%"   stopColor={sliceColor} stopOpacity="0.85" />
                      <stop offset="100%" stopColor={sliceColor} stopOpacity="1"    />
                    </linearGradient>
                  </defs>
                  <path
                    d={`M0,0 L${x1},${y1} A${RADIUS},${RADIUS} 0 ${largeArc} 1 ${x2},${y2} Z`}
                    fill={`url(#${gradId})`}
                    stroke="#fff"
                    strokeWidth="4"
                    filter="url(#pieShadow)"
                  />
                </g>
              );
            })}
            {/* Donut hole */}
            <circle r="60" fill="#fff" />
          </g>
        </svg>
      </div>
    </div>
  );

  if (noCard) return <CardContent className="p-0">{chartContent}</CardContent>;

  return (
    <Card className="bg-white rounded-2xl shadow-lg p-8 mx-auto max-w-[700px] min-w-[320px] flex flex-col items-center">
      <CardContent className="p-0">{chartContent}</CardContent>
    </Card>
  );
};

export default CustomPieChart;